import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ReturnsTrend } from "../types"
import { formatAxisMnt, formatMnt } from "../utils"
import { AnalyticsCard } from "./AnalyticsCard"
import { AXIS_LABEL, AXIS_LINE, GRID_LINE, LEGEND, SERIES, TOOLTIP } from "./chart-theme"

type Props = { data: ReturnsTrend | undefined; isLoading: boolean }

export function ReturnsTrendChart({ data, isLoading }: Props) {
  const { t } = useTranslation(["dashboard"])

  const options = useMemo<Highcharts.Options>(() => {
    const rows = data?.data.rows ?? []
    return {
      chart  : { type: "column", backgroundColor: "transparent", height: 260, style: { fontFamily: "inherit" } },
      title  : { text: undefined },
      credits: { enabled: false },
      accessibility: { enabled: false },
      legend : LEGEND,
      xAxis  : { categories: rows.map(r => r.label), labels: { style: AXIS_LABEL }, lineColor: AXIS_LINE },
      yAxis  : [
        { title: { text: undefined }, gridLineColor: GRID_LINE, labels: { style: AXIS_LABEL, formatter() { return formatAxisMnt(this.value as number) } } },
        { opposite: true, title: { text: undefined }, gridLineWidth: 0, min: 0, allowDecimals: false, labels: { style: AXIS_LABEL } }
      ],
      tooltip: {
        ...TOOLTIP,
        formatter() {
          const points = this.points ?? []
          const body = points.map(p => {
            const v = p.y
            if (v === null || v === undefined) return `${p.series.name}: —`
            const shown = p.series.userOptions.yAxis === 1 ? String(v) : formatMnt(v as number)
            return `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${shown}</b>`
          }).join("<br/>")
          return `<b>${this.x}</b><br/>${body}`
        }
      },
      plotOptions: { column: { borderWidth: 0, pointPadding: 0.1, groupPadding: 0.15, maxPointWidth: 28 } },
      series: [
        { type: "column", name: t("analytics.returns.amount", { defaultValue: "Буцаалтын дүн" }), data: rows.map(r => r.amount), color: SERIES.current, borderRadius: 2, yAxis: 0 },
        { type: "line", name: t("analytics.returns.count",  { defaultValue: "Буцаалтын тоо" }), data: rows.map(r => r.count),  color: SERIES.accent, lineWidth: 2, marker: { enabled: true, radius: 3, symbol: "circle" }, yAxis: 1 }
      ]
    }
  }, [data, t])

  return (
    <AnalyticsCard
      title={t("analytics.returns.trendTitle", { defaultValue: "Буцаалтын трэнд" })}
      subtitle={t("analytics.returns.trendSubtitle", { defaultValue: "Сарын буцаалтын дүн + тоо" })}
      isLoading={isLoading}
    >
      <HighchartsReact highcharts={Highcharts} options={options} />
    </AnalyticsCard>
  )
}
